/**
 * Comprendre une demande tapée dans la recherche du Centre du CRM.
 *
 * « facturer vaudoise » ouvre une facture préremplie pour Vaudoise ;
 * « mission fhvi », une mission pour FHVI ; « nouveau client Migros », la
 * fiche avec son nom ; « assigner emma java », la mission « Développeur Java »
 * avec Emma comme ressource. On reconnaît un VERBE (ce qu'on veut faire) et
 * des CHOSES du CRM déjà chargées (client, ressource, mission, facture) —
 * aucune devinette au-delà : sans verbe reconnu, rien n'est proposé, et la
 * recherche ordinaire fait son travail. Un verbe SEUL (« facturer »,
 * « noyau ») ne propose rien non plus : l'action du cercle du même nom le fait
 * déjà, et la recherche la trouve — deux lignes pour la même chose, c'est une
 * de trop.
 *
 * Fonction pure : elle propose, l'écran exécute (en ouvrant les formulaires
 * existants — rien ne s'écrit sans un clic de plus).
 */

export const sansAccents = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const mots = (t) => sansAccents(t).replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(Boolean);

// Les verbes : un mot (ou une expression) suffit. L'ordre compte : « nouveau
// client » avant « client ».
const INTENTIONS = [
  { cle: 'nouveau_client', expressions: ['nouveau client', 'nouvelle societe', 'ajouter un client', 'ajouter client', 'creer un client', 'creer client', 'nouveau compte'] },
  { cle: 'relancer', expressions: ['relancer', 'relance'] },
  { cle: 'facturer', expressions: ['facturer', 'facture', 'factures', 'facturation', 'factur'] },
  { cle: 'assigner', expressions: ['assigner', 'assigne', 'affecter', 'affecte', 'staffer'] },
  { cle: 'mission', expressions: ['nouvelle mission', 'creer une mission', 'creer mission', 'mission', 'opportunite'] },
  { cle: 'placer', expressions: ['placer', 'placement', 'embaucher'] },
  { cle: 'cra', expressions: ['valider les cra', 'cra', 'cras', 'releves', 'releve de temps'] },
  { cle: 'noyau', expressions: ['noyau', 'relations'] },
  { cle: 'fiche', expressions: ['fiche', 'ouvrir'] },
];

const VIDES = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'l', 'a', 'au', 'aux', 'pour', 'chez', 'un', 'une', 'sur', 'et', 'd']);

// Une chose du CRM est reconnue quand un mot tapé (3 lettres au moins) commence
// un de ses mots, ou quand son nom entier apparaît. Le score préfère le nom
// le plus complètement tapé.
function reconnaitre(liste, nomDe, tapes) {
  let meilleur = null;
  for (const item of liste) {
    const leurs = mots(nomDe(item)).filter(m => m.length >= 2);
    if (!leurs.length) continue;
    let touches = 0;
    const utilises = new Set();
    for (const m of leurs) {
      const t = tapes.find(x => x.length >= 3 && !utilises.has(x) && (m === x || m.startsWith(x)));
      if (t) { touches += t.length / m.length; utilises.add(t); }
    }
    if (!touches) continue;
    const score = touches / leurs.length + touches;
    if (!meilleur || score > meilleur.score) meilleur = { item, score, utilises };
  }
  return meilleur;
}

const nomRessource = (r) => [r?.prenom, r?.nom].filter(Boolean).join(' ') || r?.email || 'Ressource';
const estPayee = (f) => ['payee', 'payée', 'paid'].includes(sansAccents(f.statut));
const enRetard = (f) => !estPayee(f) && ['envoyee', 'en_retard', 'retard'].includes(sansAccents(f.statut))
  && (sansAccents(f.statut) !== 'envoyee' || (f.echeance && new Date(f.echeance) < new Date(new Date().toDateString())));

export function comprendreDemande(texte, { clients = [], ressources = [], missions = [], factures = [] } = {}) {
  const brut = String(texte || '').trim();
  const norme = ` ${mots(brut).join(' ')} `;
  if (norme.trim().length < 3) return [];

  // 1. Le verbe — la première intention dont une expression est tapée.
  let intention = null, expression = null;
  for (const i of INTENTIONS) {
    const e = i.expressions.find(x => norme.includes(` ${x} `) || (x.length >= 5 && norme.includes(` ${x}`)));
    if (e) { intention = i.cle; expression = e; break; }
  }
  if (!intention) return [];
  const reste = mots(brut).filter(m => !expression.split(' ').includes(m) && !VIDES.has(m));

  // 2. Les choses nommées.
  const client = reconnaitre(clients, c => c.nom, reste)?.item || null;
  const ressource = reconnaitre(ressources, nomRessource, reste)?.item || null;
  const numero = /\b(f|av)-?\d{4}-?\d{2,4}\b/i.exec(brut)?.[0];
  const facture = numero ? factures.find(f => sansAccents(f.numero).replace(/-/g, '') === sansAccents(numero).replace(/-/g, '')) : null;
  const duClient = (x) => client && ((x.client_id != null && String(x.client_id) === String(client.id)) || sansAccents(x.client).trim() === sansAccents(client.nom).trim());

  const p = [];
  const proposer = (cle, libelle, detail, icone, action) => p.push({ cle, libelle, detail, icone, action });

  switch (intention) {
    case 'nouveau_client': {
      // Le nom, tel qu'il a été tapé (sa casse comprise), après l'expression.
      const i = sansAccents(brut).indexOf(expression.split(' ').pop());
      const nom = i >= 0 ? brut.slice(i + expression.split(' ').pop().length).trim() : '';
      if (nom) proposer('nouveau_client', `Créer la fiche « ${nom} »`, 'Ouvre le formulaire, le nom déjà saisi', 'person-fill-add', { type: 'nouveau_client', nom });
      break;
    }
    case 'facturer':
      if (client) proposer('facturer', `Facturer ${client.nom}`, 'Ouvre une facture préremplie pour ce client', 'send', { type: 'facturer', client });
      break;
    case 'relancer': {
      const cibles = facture ? [facture] : factures.filter(f => enRetard(f) && (!client || duClient(f)));
      for (const f of cibles.slice(0, 3)) proposer(`relancer-${f.id}`, `Relancer ${f.numero || 'la facture'}`, `${f.client || ''} — ouvre la facture, « Renvoyer au client »`.trim(), 'receipt', { type: 'facture', facture: f });
      if (!cibles.length) proposer('relancer-aucune', client ? `Aucune facture en retard chez ${client.nom}` : 'Aucune facture en retard', 'Rien à relancer', 'check2', { type: 'rien' });
      break;
    }
    case 'mission':
      if (client) proposer('mission', `Créer une mission pour ${client.nom}`, 'Ouvre le formulaire de mission, le client choisi', 'briefcase', { type: 'mission', client });
      break;
    case 'assigner': {
      // La mission : par ses mots (« java »), sinon une mission sans ressource
      // du client nommé, sinon les missions sans ressource.
      const libres = missions.filter(m => m.resource_id == null && ['active', 'draft', 'brouillon'].includes(sansAccents(m.statut)));
      const parMots = reconnaitre(libres, m => m.titre, reste.filter(m => !ressource || !mots(nomRessource(ressource)).some(x => x.startsWith(m))))?.item;
      const cibles = parMots ? [parMots] : libres.filter(m => !client || duClient(m)).slice(0, 2);
      for (const m of cibles) {
        proposer(`assigner-${m.id}`, ressource ? `Assigner ${nomRessource(ressource)} à « ${m.titre} »` : `Assigner « ${m.titre} »`,
          `${m.client || ''} — ouvre la mission${ressource ? ', la ressource choisie' : ''}`.trim(), 'people-fill', { type: 'assigner', mission: m, ressource });
      }
      if (!cibles.length) proposer('assigner-aucune', 'Toutes les missions sont pourvues', 'Rien à assigner', 'check2', { type: 'rien' });
      break;
    }
    case 'placer':
      // Rien à nommer : l'action « Placer un candidat » du cercle le fait.
      break;
    case 'cra':
      if (ressource) proposer('cra', `Valider les CRA de ${nomRessource(ressource)}`, 'Ouvre les relevés de temps du mois', 'clock', { type: 'cra', ressource });
      break;
    case 'noyau':
      if (client) proposer('noyau', `Voir les relations de ${client.nom}`, 'Ouvre le noyau sur ce client', 'eye', { type: 'noyau', client });
      break;
    case 'fiche':
      if (client) proposer('fiche', `Ouvrir la fiche ${client.nom}`, 'La fiche client', 'buildings', { type: 'fiche', client });
      else if (facture) proposer('fiche-facture', `Ouvrir ${facture.numero}`, facture.client || 'La facture', 'receipt', { type: 'facture', facture });
      break;
    default:
  }
  return p;
}
